/**
 * Scroll restoration keyed by route (t184u). Lists that open an item on its own route (the feed, Chat, Tasks, the rota
 * day) unmount when you go in; coming back with Back, a swipe back or the browser's Back (a POP navigation) puts the
 * list exactly where it was, once its content has rendered. A fresh visit (a link or a tab, PUSH) starts at the top.
 *
 * Positions live in memory and in sessionStorage (so a reload of the tab keeps them). Saved on scroll, never on
 * unmount: by then the element is detached and reads 0. Restoring retries for a while (pages from the query cache,
 * images and comments can still be laying out) and gives up as soon as the person scrolls themselves.
 *
 * t298u (desktop Home): exact restoration.
 * - `anchor` names the data attribute on each list item (e.g. `data-feed-id`). Besides the raw offset, the hook saves
 *   which item is at the top of the view and how far down it starts, and restores that item to the same spot. Cards
 *   above it that lay out late (comments, photos) can't push the view.
 * - The snapshot is also written into the history entry (history.state), so each Back lands on its own entry's place.
 * - `restoreOnPushWithin`: a fresh visit (the Home link) also comes back to the same place when you left it less than
 *   this long ago; clicking it again while you are already there goes to the top.
 */
import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const PREFIX = 'scroll:';
const memory = new Map<string, number>();

export const scrollStore = {
  get(key: string): number {
    if (memory.has(key)) return memory.get(key)!;
    try { const v = Number(sessionStorage.getItem(PREFIX + key)); return Number.isFinite(v) ? v : 0; } catch { return 0; }
  },
  set(key: string, top: number) {
    memory.set(key, top);
    try { sessionStorage.setItem(PREFIX + key, String(Math.round(top))); } catch { /* private mode */ }
  },
  clear() { memory.clear(); anchors.clear(); },
};

/** Which item was at the top of the view, how far below the top of the list it started, and when that was. */
export interface ScrollAnchor { top: number; id: string | null; offset: number; at: number }
const APREFIX = 'scroll-anchor:';
const anchors = new Map<string, ScrollAnchor>();
export const anchorStore = {
  get(key: string): ScrollAnchor | null {
    if (anchors.has(key)) return anchors.get(key)!;
    try { const v = JSON.parse(sessionStorage.getItem(APREFIX + key) ?? 'null') as ScrollAnchor | null; return v && typeof v.top === 'number' ? v : null; } catch { return null; }
  },
  set(key: string, a: ScrollAnchor | null) {
    if (a) anchors.set(key, a); else anchors.delete(key);
    try { if (a) sessionStorage.setItem(APREFIX + key, JSON.stringify(a)); else sessionStorage.removeItem(APREFIX + key); } catch { /* private mode */ }
  },
};

/** The route key: path and query (e.g. `/rota?date=2026-10-21`), plus an optional list name when one route has two. */
export const routeScrollKey = (pathname: string, search: string, name?: string) => `${pathname}${search}${name ? `#${name}` : ''}`;

/** The first item (by `attr`) whose bottom is below the top of the view, and how far below the view's top it starts. */
export function findAnchor(el: HTMLElement, attr: string): { id: string; offset: number } | null {
  const top = el.getBoundingClientRect().top;
  for (const n of el.querySelectorAll<HTMLElement>(`[${attr}]`)) {
    const r = n.getBoundingClientRect();
    if (r.bottom > top + 1) return { id: n.getAttribute(attr)!, offset: r.top - top };
  }
  return null;
}
/** The scrollTop that puts item `id` at `offset` below the top of the view, or null while it isn't rendered. */
export function anchorTarget(el: HTMLElement, attr: string, id: string, offset: number): number | null {
  const n = el.querySelector<HTMLElement>(`[${attr}="${CSS.escape(id)}"]`);
  if (!n) return null;
  return el.scrollTop + (n.getBoundingClientRect().top - el.getBoundingClientRect().top) - offset;
}

/** Will this visit restore (rather than start at the top)? The list can then keep its cached pages as they are. */
export function willRestoreScroll(key: string, navType: string, restoreOnPushWithin?: number, now = Date.now()): boolean {
  if (navType === 'POP') return true;
  if (!restoreOnPushWithin) return false;
  const a = anchorStore.get(key);
  return Boolean(a && a.top > 0 && now - a.at < restoreOnPushWithin);
}

type HistState = { key?: string; scroll?: Record<string, ScrollAnchor> } & Record<string, unknown>;
const histState = (): HistState | null => { try { return (window.history.state as HistState | null) ?? null; } catch { return null; } };

export interface RestoreOptions {
  /** True once the list has its data (cached pages count): restoring waits for it. */
  ready: boolean;
  /** Overrides the route key (e.g. the desktop Home page keeps its historical key). */
  key?: string;
  /** A second list on the same route. */
  name?: string;
  /** How long to keep trying while the content lays out. */
  timeoutMs?: number;
  /** t298u: the data attribute that marks list items (e.g. `data-feed-id`), to restore by item rather than by pixels. */
  anchor?: string;
  /** t298u: a fresh visit restores too when the list was left less than this many ms ago. */
  restoreOnPushWithin?: number;
}

/**
 * Restore `ref`'s scroll on Back and remember it as the person scrolls. Returns the key in use (for tests).
 */
export function useScrollRestoration(ref: RefObject<HTMLElement>, { ready, key: fixed, name, timeoutMs = 3000, anchor, restoreOnPushWithin }: RestoreOptions): string {
  const loc = useLocation();
  const navType = useNavigationType();
  const key = fixed ?? routeScrollKey(loc.pathname, loc.search, name);
  const restoring = useRef(false);
  const done = useRef<string | null>(null);
  // Remember the position as the person scrolls (rAF-throttled); ignore the scroll events our own restore causes.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let frame = 0;
    let hist = 0;
    let entry: string | undefined;
    const snap = (): ScrollAnchor => {
      const a = anchor ? findAnchor(el, anchor) : null;
      return { top: el.scrollTop, id: a?.id ?? null, offset: a?.offset ?? 0, at: Date.now() };
    };
    // Into this history entry's state, only while it is still the current entry (a click may have navigated away).
    const toHistory = () => {
      hist = 0;
      const s = histState();
      if (!anchor || !s || s.key !== entry) return;
      try { window.history.replaceState({ ...s, scroll: { ...(s.scroll ?? {}), [key]: anchorStore.get(key) ?? snap() } }, ''); } catch { /* rate limited */ }
    };
    const save = () => {
      if (restoring.current || frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0; scrollStore.set(key, el.scrollTop);
        if (anchor) { anchorStore.set(key, snap()); if (!hist) { entry = histState()?.key; hist = window.setTimeout(toHistory, 250); } }
      });
    };
    // A click inside the list (opening an item) records the exact place before the route changes.
    const flush = () => { if (restoring.current) return; scrollStore.set(key, el.scrollTop); if (anchor) { anchorStore.set(key, snap()); window.clearTimeout(hist); entry = histState()?.key; toHistory(); } };
    el.addEventListener('scroll', save, { passive: true });
    el.addEventListener('click', flush, { capture: true });
    return () => { el.removeEventListener('scroll', save); el.removeEventListener('click', flush, { capture: true }); if (frame) cancelAnimationFrame(frame); window.clearTimeout(hist); };
  }, [key, ref, anchor]);
  // A fresh visit starts at the top (the element may be reused across routes), unless it restores on a fresh visit too.
  const firstLoc = useRef(loc.key);
  useLayoutEffect(() => {
    if (navType === 'POP' || willRestoreScroll(key, navType, restoreOnPushWithin)) return;
    done.current = key; scrollStore.set(key, 0); anchorStore.set(key, null); ref.current?.scrollTo?.({ top: 0 });
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  // The same link clicked again while you're here (a new entry, same key): back to the top.
  useEffect(() => {
    if (loc.key === firstLoc.current) return;
    firstLoc.current = loc.key;
    if (navType === 'PUSH' && done.current === key) { scrollStore.set(key, 0); anchorStore.set(key, null); ref.current?.scrollTo?.({ top: 0, behavior: 'smooth' }); }
  }, [loc.key]); // eslint-disable-line react-hooks/exhaustive-deps
  // Back: restore after render, once the data is there.
  useEffect(() => {
    const el = ref.current;
    if (!el || !ready || done.current === key) return;
    done.current = key;
    const own = navType === 'POP' ? histState()?.scroll?.[key] : undefined;
    const a = anchor ? own ?? anchorStore.get(key) : null;
    const y = a?.top ?? scrollStore.get(key);
    if (!y) return;
    restoring.current = true;
    const until = performance.now() + timeoutMs;
    let stop = false, frame = 0, stable = 0, finished = false;
    const cancel = () => { stop = true; restoring.current = false; };
    el.addEventListener('wheel', cancel, { passive: true, once: true });
    el.addEventListener('touchstart', cancel, { passive: true, once: true });
    el.addEventListener('pointerdown', cancel, { once: true });
    el.addEventListener('keydown', cancel, { once: true });
    const go = () => {
      if (stop) return;
      // By item when it is rendered (cards above it may still change height); by pixels until then.
      const want = a?.id && anchor ? anchorTarget(el, anchor, a.id, a.offset) ?? y : y;
      if (Math.abs(el.scrollTop - want) > 1) el.scrollTop = want;
      // Reached and held for a few frames (late images or comments can push it), or out of time. With an anchor it keeps
      // holding the item in place until the time is up or you scroll.
      stable = Math.abs(el.scrollTop - want) <= 2 ? stable + 1 : 0;
      if ((anchor ? true : stable < 10) && performance.now() < until) frame = requestAnimationFrame(go);
      else { finished = true; restoring.current = false; scrollStore.set(key, el.scrollTop); }
    };
    frame = requestAnimationFrame(go);
    return () => {
      // Torn down before it finished (a re-render, React's strict double-run): try again on the next run.
      if (!finished && !stop) done.current = null;
      stop = true; restoring.current = false; cancelAnimationFrame(frame);
      el.removeEventListener('wheel', cancel); el.removeEventListener('touchstart', cancel); el.removeEventListener('pointerdown', cancel); el.removeEventListener('keydown', cancel);
    };
  }, [key, ready, ref, timeoutMs]); // eslint-disable-line react-hooks/exhaustive-deps
  return key;
}
